/**
 * Snapshot adapters for the shared Table/Pitch preference-order controller.
 *
 * Moved byte-for-byte out of `useWatchlistPreferenceOrder.ts` (SE-8389) so the
 * save-recovery API (Retry / Discard / Reload / Re-apply) fits in the hook.
 */
import {
  createPitchFormationOperation,
  heldSlotsByPlayer,
  type WatchlistPitchAssignment,
  type WatchlistPitchAssignmentOrigin,
  type WatchlistPitchFormationOperation,
  type WatchlistPreferenceSnapshot,
} from "./watchlistPreferenceOrderState";

/** Read and mutation fields used to create an authoritative snapshot. */
interface PreferenceOrderSnapshotSource {
  readonly pitchRevision: number;
  readonly manualOrder: boolean;
  readonly pitchInitialized?: boolean;
  readonly formation?: string;
  readonly members?: {
    readonly edges: ReadonlyArray<{
      readonly node: {
        readonly playerId: string;
        readonly effectiveSlot?: string | null;
        readonly assignmentOrigin?: string;
      };
    }>;
  };
  readonly orderedPlayers?: ReadonlyArray<{ readonly playerId: string }>;
}

/**
 * Converts a Pitch read or mutation payload into controller state.
 * @param value - Authoritative Pitch read or mutation payload
 * @returns Immutable preference-order snapshot
 */
export const toSnapshot = (
  value: PreferenceOrderSnapshotSource
): WatchlistPreferenceSnapshot => ({
  orderedPlayerIds:
    value.orderedPlayers?.map(player => player.playerId) ??
    value.members?.edges.map(edge => edge.node.playerId) ??
    [],
  pitchRevision: value.pitchRevision,
  manualOrder: value.manualOrder,
  pitchInitialized: value.pitchInitialized ?? false,
  formation: value.formation,
  assignmentsByPlayerId: Object.fromEntries(
    value.members?.edges.map(({ node }) => [
      node.playerId,
      {
        tacticalSlot: node.effectiveSlot ?? null,
        origin:
          (node.assignmentOrigin as WatchlistPitchAssignmentOrigin) ??
          (node.effectiveSlot ? "CALCULATED" : "UNASSIGNED"),
      },
    ]) ?? []
  ),
});

/**
 * Each member's primary-position code, the input a formation change needs to
 * re-project the Unassigned the way the backend does (SE-8606).
 * @param edges - The member edges of a complete Pitch read
 * @returns Primary-position code (or null) by player id
 */
export const primaryPositionsOf = (
  edges: readonly {
    readonly node: {
      readonly playerId: string;
      readonly primaryPositionCode?: string | null;
    };
  }[]
): Readonly<Record<string, string | null>> =>
  Object.fromEntries(
    edges.map(({ node }) => [node.playerId, node.primaryPositionCode ?? null])
  );

/**
 * The optimistic operation of one watchlist formation change. It records the
 * slots held right now, so the Unassigned panel can say "was {slot}" for
 * whoever the target formation orphans (SE-8391), and each member's primary
 * position, so the projection re-places the Unassigned the way the backend
 * does (SE-8606).
 * @param id - Operation identity
 * @param formation - Target formation
 * @param slots - Complete slot list of the target formation
 * @param assignments - Effective assignments before the change
 * @param edges - The member edges of a complete Pitch read
 * @returns The formation operation
 */
export const watchlistFormationOperation = (
  id: string,
  formation: string,
  slots: readonly string[],
  assignments: Readonly<Record<string, WatchlistPitchAssignment>>,
  edges: Parameters<typeof primaryPositionsOf>[0]
): WatchlistPitchFormationOperation =>
  createPitchFormationOperation(
    id,
    formation,
    slots,
    heldSlotsByPlayer(assignments),
    primaryPositionsOf(edges)
  );

/** Assignment-shaped mutation payloads (assignment and formation writes). */
interface PitchAssignmentPayload {
  readonly pitchRevision: number;
  readonly pitchInitialized: boolean;
  readonly formation?: string | null;
  readonly assignments: ReadonlyArray<{
    readonly playerId: string;
    readonly rank: number;
    readonly tacticalSlot?: string | null;
  }>;
}

/**
 * Adopts an assignment-shaped payload over the confirmed order.
 * @param confirmed - Snapshot the write was issued against
 * @param payload - Authoritative payload returned by the backend
 * @returns Next confirmed snapshot; rank and manual mode stay as confirmed
 */
export const snapshotFromAssignmentPayload = (
  confirmed: WatchlistPreferenceSnapshot,
  payload: PitchAssignmentPayload
): WatchlistPreferenceSnapshot => ({
  orderedPlayerIds: confirmed.orderedPlayerIds,
  pitchRevision: payload.pitchRevision,
  manualOrder: confirmed.manualOrder,
  pitchInitialized: payload.pitchInitialized,
  formation: payload.formation ?? confirmed.formation,
  assignmentsByPlayerId: Object.fromEntries(
    [...payload.assignments]
      .sort((left, right) => left.rank - right.rank)
      .map(item => [
        item.playerId,
        {
          tacticalSlot: item.tacticalSlot ?? null,
          origin: item.tacticalSlot ? "SAVED" : "UNASSIGNED",
        },
      ])
  ),
});
